import { useEffect, useRef, useCallback } from 'react';
import { useConfigStore } from '@/store';

export function useAutoScroll(containerRef: React.RefObject<HTMLElement | null>) {
  const { autoscrollEnabled, autoscrollInterval, routesPerRow } = useConfigStore();
  const currentRowIndex = useRef(0);

  const scrollToNextRow = useCallback(() => {
    const container = containerRef.current;
    if (!container) return;

    const routes = container.querySelectorAll('[data-route-card]');
    const visibleRoutes = Array.from(routes).filter(
      (route) => (route as HTMLElement).offsetParent !== null
    );

    if (visibleRoutes.length === 0) return;

    const totalRows = Math.ceil(visibleRoutes.length / routesPerRow);

    if (totalRows <= 1) return; // No need to scroll if only one row

    // Move to next row, or reset to top
    currentRowIndex.current = (currentRowIndex.current + 1) % totalRows;

    // Calculate scroll position based on row height
    const firstRoute = visibleRoutes[0] as HTMLElement;
    const rowHeight = firstRoute.offsetHeight;
    const scrollPosition = currentRowIndex.current * rowHeight;

    container.scrollTo({
      top: scrollPosition,
      behavior: 'smooth',
    });
  }, [containerRef, routesPerRow]);

  const reset = useCallback(() => {
    currentRowIndex.current = 0;
    const container = containerRef.current;
    if (container) {
      container.scrollTo({ top: 0, behavior: 'smooth' });
    }
  }, [containerRef]);

  useEffect(() => {
    if (!autoscrollEnabled) {
      // Reset to top when disabled
      reset();
      return;
    }

    const intervalId = setInterval(scrollToNextRow, autoscrollInterval * 1000);

    return () => clearInterval(intervalId);
  }, [autoscrollEnabled, autoscrollInterval, scrollToNextRow, reset]);

  return { reset };
}
